import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Search } from "lucide-react";
import dayjs from "dayjs";
import Api from "../Api";
import Logo from "../assets/logo.png";

const kondisiClass = (kondisi) => {
  switch (String(kondisi || "").toLowerCase()) {
    case "baik":
    case "normal":
      return "bg-green-100 text-green-800";
    case "rusak ringan":
      return "bg-yellow-100 text-yellow-800";
    case "rusak berat":
    case "rusak":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

const kondisiLabel = (value) => {
  const text = String(value || "baik").trim();
  if (!text) return "Baik";
  return text.charAt(0).toUpperCase() + text.slice(1);
};

const tahunPerolehan = (value) => {
  if (!value) return "-";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("YYYY") : "-";
};

const dash = (value) => {
  const text = String(value ?? "").trim();
  return text || "-";
};

const EMPTY_ITEMS = [];

function KirPage() {
  const { ruangan } = useParams();
  const roomKey = decodeURIComponent(ruangan || "").trim();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (!roomKey) {
        setDetail(null);
        setError("Ruangan tidak ditemukan.");
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");
      try {
        const res = await Api.get(`/kir/${encodeURIComponent(roomKey)}`);
        if (!active) return;
        setDetail(res.data?.data || null);
      } catch (err) {
        if (!active) return;
        setDetail(null);
        setError(err.response?.data?.message || "Gagal memuat daftar inventaris ruangan.");
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, [roomKey]);

  const items = detail?.items ?? EMPTY_ITEMS;
  const room = detail?.ruangan;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => {
      const haystack = [item.kode, item.kode_ypt, item.desc, item.merk, item.type, item.noseri, item.spec]
        .map((part) => String(part || "").toLowerCase())
        .join(" ");
      return haystack.includes(needle);
    });
  }, [items, query]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-red-600 mb-4"></div>
          <p className="text-gray-600 font-medium">Memuat kartu inventaris ruangan...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <div className="px-5 py-5 border-b border-gray-100">
            <img src={Logo} alt="Logo" className="w-[150px] h-10 mb-4" />
            <p className="text-xs font-semibold uppercase tracking-wide text-red-700">Kartu Inventaris Ruangan</p>
            <h1 className="text-2xl font-bold text-gray-900 mt-1">{room?.ruangan || "Ruangan"}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {room?.gedung || "-"}
              {room?.jenis ? ` · ${room.jenis}` : ""}
              {` · ${items.length} barang`}
            </p>
          </div>

          {error && (
            <div className="px-5 py-10 text-center">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Data ruangan tidak tersedia</h2>
              <p className="text-sm text-gray-500">{error}</p>
            </div>
          )}

          {!error && (
            <>
              <div className="px-5 py-4">
                <label className="relative block">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Cari kode, nama, merk, atau nomor seri"
                    className="w-full bg-gray-50 border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                </label>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-[980px] w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-3 text-center font-semibold w-12">No</th>
                      <th className="px-3 py-3 text-left font-semibold">Nama/Jenis Barang</th>
                      <th className="px-3 py-3 text-left font-semibold">Merk/Model</th>
                      <th className="px-3 py-3 text-left font-semibold">No. Seri</th>
                      <th className="px-3 py-3 text-left font-semibold">Kode Barang</th>
                      <th className="px-3 py-3 text-center font-semibold">Jumlah</th>
                      <th className="px-3 py-3 text-center font-semibold">Tahun</th>
                      <th className="px-3 py-3 text-center font-semibold">Kondisi</th>
                      <th className="px-3 py-3 text-left font-semibold">Asal-Usul</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="px-4 py-10 text-center text-gray-500">
                          {items.length === 0
                            ? "Belum ada barang pada ruangan ini."
                            : "Tidak ada barang yang cocok dengan pencarian."}
                        </td>
                      </tr>
                    ) : (
                      filtered.map((item, index) => (
                        <tr key={item.id || index} className="border-t border-gray-100 align-top">
                          <td className="px-3 py-3 text-center text-gray-500">{index + 1}</td>
                          <td className="px-3 py-3 text-gray-900">
                            <div className="font-medium">{dash(item.desc)}</div>
                            {item.spec && <div className="text-xs text-gray-500 mt-1">{item.spec}</div>}
                          </td>
                          <td className="px-3 py-3 text-gray-700">
                            {dash([item.merk, item.type].filter(Boolean).join(" / "))}
                          </td>
                          <td className="px-3 py-3 text-gray-700">{dash(item.noseri)}</td>
                          <td className="px-3 py-3">
                            {item.kode ? (
                              <Link to={`/${encodeURIComponent(item.kode)}`} className="font-semibold text-red-700 hover:underline">
                                {item.kode}
                              </Link>
                            ) : (
                              "-"
                            )}
                            {item.kode_ypt && <div className="text-xs text-gray-500 mt-1">YPT: {item.kode_ypt}</div>}
                          </td>
                          <td className="px-3 py-3 text-center text-gray-700">
                            {item.jml ?? "-"} {item.satuan || ""}
                          </td>
                          <td className="px-3 py-3 text-center text-gray-700">{tahunPerolehan(item.tgl)}</td>
                          <td className="px-3 py-3 text-center">
                            <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${kondisiClass(item.kondisi)}`}>
                              {kondisiLabel(item.kondisi)}
                            </span>
                          </td>
                          <td className="px-3 py-3 text-gray-700">{dash(item.sumber)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default KirPage;
